import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';

const GuidesContext = createContext();

export const useGuides = () => {
  const context = useContext(GuidesContext);
  if (!context) {
    throw new Error('useGuides must be used within a GuidesProvider');
  }
  return context;
};

export const GuidesProvider = ({ children }) => {
  const { currentUser } = useAuth();

  // Initialize cache from sessionStorage (for Android persistence)
  const initializeCache = () => {
    try {
      const stored = sessionStorage.getItem('guidesCache');
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.warn('Failed to load guides cache from sessionStorage:', error);
      return {};
    }
  };

  const initializeLastFetchTime = () => {
    try {
      const stored = sessionStorage.getItem('guidesLastFetchTime');
      return stored ? new Date(stored) : null;
    } catch (error) {
      console.warn('Failed to load last fetch time from sessionStorage:', error);
      return null;
    }
  };

  const [guidesCache, setGuidesCache] = useState(initializeCache);
  const [lastFetchTime, setLastFetchTime] = useState(initializeLastFetchTime);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // API URL
  const API_BASE_URL = 'https://us-central1-guidesofia-access-control.cloudfunctions.net';

  // Detect platform for debugging
  const isAndroid = /Android/i.test(navigator.userAgent);
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

  const fetchGuides = useCallback(async (selectedCountry = 'all', forceRefresh = false) => {
    const cacheKey = selectedCountry || 'all';

    // Return cached data if available and not forcing refresh
    if (guidesCache[cacheKey] && !forceRefresh) {
      return {
        guides: guidesCache[cacheKey],
        fromCache: true
      };
    }

    try {
      setLoading(true);
      setError(null);

      // Don't fetch guides if user is not authenticated
      if (!currentUser) {
        return { guides: [], fromCache: false };
      }

      const url = selectedCountry === 'all'
        ? `${API_BASE_URL}/getGuides`
        : `${API_BASE_URL}/getGuides?country=${selectedCountry}`;

      // Get auth token for the current user
      const token = await currentUser.getIdToken(true);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        // Cache the guides
        const newCache = {
          ...guidesCache,
          [cacheKey]: data.guides
        };
        setGuidesCache(newCache);

        const fetchTime = new Date();
        setLastFetchTime(fetchTime);

        // Persist to sessionStorage for Android
        try {
          sessionStorage.setItem('guidesCache', JSON.stringify(newCache));
          sessionStorage.setItem('guidesLastFetchTime', fetchTime.toISOString());
        } catch (error) {
          console.warn('Failed to persist guides cache to sessionStorage:', error);
        }

        return {
          guides: data.guides,
          fromCache: false
        };
      } else {
        const errorMsg = data.error || data.message || `Failed to fetch guides (${response.status})`;
        setError(errorMsg);
        throw new Error(errorMsg);
      }
    } catch (err) {
      const errorMsg = err?.message || 'Failed to connect to server';
      setError(errorMsg);
      console.error('Error fetching guides:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentUser, guidesCache]);

  const getCachedGuides = useCallback((selectedCountry = 'all') => {
    const cacheKey = selectedCountry || 'all';
    return guidesCache[cacheKey] || null;
  }, [guidesCache]);

  const clearCache = useCallback(() => {
    setGuidesCache({});
    setLastFetchTime(null);
    setError(null);

    // Also clear sessionStorage
    try {
      sessionStorage.removeItem('guidesCache');
      sessionStorage.removeItem('guidesLastFetchTime');
    } catch (error) {
      console.warn('Failed to clear guides cache from sessionStorage:', error);
    }
  }, []);

  const value = {
    guidesCache,
    lastFetchTime,
    loading,
    error,
    fetchGuides,
    getCachedGuides,
    clearCache
  };

  return (
    <GuidesContext.Provider value={value}>
      {children}
    </GuidesContext.Provider>
  );
};
